import { useUserStore } from '@/entities/user';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import type { CommentInputProps } from './commentInput.types';

/**
 * Drives the comment composer of a support request. Reads the signed-in user from the user store as the comment author, keeps the draft text in state, disables sending while the trimmed content is empty, and clears the draft once onSubmit resolves.
 *
 * @param onSubmit - Prop invoked with the comment content when sending.
 * @returns The current user, draft state, the disabled flag and the change and send handlers.
 */
export const useCommentInput = ({ onSubmit }: CommentInputProps) => {
  /**
   * Currently signed-in user shown as the comment author. Read from the user store through a selector.
   *
   * @returns The current user record held in the store.
   */
  const meRedux = useUserStore((s) => s.me);

  const [state, setState] = useSetState({
    content: '',
    isSaving: false,
  });

  const isDisabled = _.isEmpty(state.content.trim());

  /**
   * Stores the typed support-request comment text. Writes the value into state.content, ignoring the field name.
   *
   * @param name - Input field name, unused.
   * @param value - Current textarea content.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ content: value });
  };

  /**
   * Submits the composed support request comment. Raises isSaving, awaits onSubmit with the current content, then clears both the text and the saving flag.
   *
   * @returns Promise resolving once the comment was submitted and the draft reset.
   */
  const onClickSend = async () => {
    setState({ isSaving: true });
    await onSubmit(state.content);
    setState({
      content: '',
      isSaving: false,
    });
  };

  return {
    meRedux,
    state,
    isDisabled,
    onChange,
    onClickSend,
  };
};
